import React, { createContext, useContext, useState, useEffect } from 'react';

const LocaleContext = createContext();

export const REGIONS = {
  MX: {
    code: 'MX',
    name: 'México',
    flag: '🇲🇽',
    language: 'es',
    currency: 'MXN',
    currencySymbol: '$',
    currencyName: 'Pesos mexicanos'
  },
  US: {
    code: 'US',
    name: 'United States',
    flag: '🇺🇸',
    language: 'en',
    currency: 'USD',
    currencySymbol: '$',
    currencyName: 'US Dollars'
  }
};

export const PRICING = {
  MX: {
    pro: { 
      price: 599, 
      period: 'mes', 
      priceId: 'price_1REXWi2EhLTWalzygwWEf81E',
      aiCredits: 100
    },
    enterprise: {
      price: 1499,
      period: 'mes',
      aiCredits: 500
    },
    credits: [
      { id: 'credits_50', credits: 50, price: 99 },
      { id: 'credits_150', credits: 150, price: 249, popular: true },
      { id: 'credits_400', credits: 400, price: 549 }
    ]
  },
  US: {
    pro: { 
      price: 35, 
      period: 'month', 
      priceId: 'price_us_pro',
      aiCredits: 100
    },
    enterprise: {
      price: 89,
      period: 'month',
      aiCredits: 500
    },
    credits: [
      { id: 'credits_50', credits: 50, price: 5.99 },
      { id: 'credits_150', credits: 150, price: 14.99, popular: true },
      { id: 'credits_400', credits: 400, price: 32.99 }
    ]
  }
};

export const LocaleProvider = ({ children }) => {
  const [region, setRegion] = useState(() => {
    // Check localStorage first
    const saved = localStorage.getItem('texhub_region');
    if (saved && REGIONS[saved]) return saved;
    
    // Try to detect from browser
    const browserLang = navigator.language || navigator.userLanguage;
    if (browserLang?.startsWith('en')) return 'US';
    return 'MX';
  });

  const currentRegion = REGIONS[region];
  const language = currentRegion.language;
  const currency = currentRegion.currency;

  useEffect(() => {
    localStorage.setItem('texhub_region', region);
    document.documentElement.lang = language;
  }, [region, language]);

  const changeRegion = (newRegion) => {
    if (REGIONS[newRegion]) {
      setRegion(newRegion);
    }
  };

  // Sync region with user's country (called after login)
  const syncWithUserCountry = (userCountry) => {
    if (userCountry === 'US' || userCountry === 'USA') {
      setRegion('US');
    } else if (userCountry === 'MX' || userCountry === 'México' || userCountry === 'Mexico') {
      setRegion('MX');
    }
  };

  const formatPrice = (amount, showCurrency = true) => {
    if (amount === null || amount === undefined) return null;
    const formatted = new Intl.NumberFormat(language === 'es' ? 'es-MX' : 'en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
    return showCurrency ? `${currentRegion.currencySymbol}${formatted} ${currency}` : `${currentRegion.currencySymbol}${formatted}`;
  };

  const getPricing = (plan) => {
    return PRICING[region]?.[plan] || PRICING.MX[plan];
  };

  const value = {
    region,
    currentRegion,
    language,
    currency,
    changeRegion,
    syncWithUserCountry,
    formatPrice,
    getPricing,
    isUSA: region === 'US',
    isMexico: region === 'MX'
  };

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
};

export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
};

export default LocaleContext;
